
import type * as ClassTypes from './types/class';
import {
    initAnimation,
    initCallbackPopup,
    initCarousel,
    initCarouselSlider,
    initContentSlider,
    initFirstScreenSlider,
    initFlatCard,
    initFlatFeatures,
    initGalleryDropdown,
    initHeader,
    initInfoPoint,
    initInfoSlider,
    initLoader,
    initPopup,
    initPromoSlider,
    initReviewsSlider,
    initSlider,
    initSliderFinishing,
    initSliderMini,
    initSliderVertical,
    initStickySlider,
    initTabs,
    initTogglers
} from './common';
import type {IDynamicImport} from './types/utils';
import type {IObserver} from './types/observer';
import Observer from './observer';

const observer: IObserver = new Observer();

// Анимация
initAnimation();

// Поведение шапки
const headerWrap = document.querySelector('.j-header') as HTMLElement;

if (headerWrap) {
    initHeader(headerWrap);
}

// Туглеры
const togglers = [...document.querySelectorAll('.j-toggle-wrapper')] as HTMLElement[];

togglers.forEach((elem: HTMLElement) => {
    initTogglers(elem);
});

// Carousel
const carousel = [...document.querySelectorAll('.j-carousel-wrapper')] as HTMLElement[];

carousel.forEach((wrapper: HTMLElement) => {
    initCarousel(wrapper);
});

// first-screen
const firstScreenSlider = [...document.querySelectorAll('.j-first-screen')] as HTMLElement[];

firstScreenSlider.forEach((wrapper: HTMLElement) => {
    initFirstScreenSlider(wrapper);
});

// Слайдер
const slider = [...document.querySelectorAll('.j-slider-wrapper')] as HTMLElement[];

slider.forEach((wrapper: HTMLElement) => {
    initSlider(wrapper);
});

// Слайдер
const contentSlider = [...document.querySelectorAll('.j-content-slider-wrapper')] as HTMLElement[];

contentSlider.forEach((wrapper: HTMLElement) => {
    initContentSlider(wrapper);
});

// Слайдер
const reviewsSlider = [...document.querySelectorAll('.j-reviews-slider-wrapper')] as HTMLElement[];

reviewsSlider.forEach((wrapper: HTMLElement) => {
    initReviewsSlider(wrapper);
});

// Flat features accordion
const flatFeatures = [...document.querySelectorAll('.j-flat-features')] as HTMLElement[];

flatFeatures.forEach((wrapper: HTMLElement) => {
    initFlatFeatures(wrapper);
});

// Промо-слайдер
const promoSlider = [...document.querySelectorAll('.j-promo-slider-wrapper')] as HTMLElement[];

promoSlider.forEach((wrapper: HTMLElement) => {
    initPromoSlider(wrapper);
});

// Слайдер
const carouselSlider = [...document.querySelectorAll('.j-carousel-slider-wrapper')] as HTMLElement[];

carouselSlider.forEach((wrapper: HTMLElement) => {
    initCarouselSlider(wrapper);
});

// Слайдер mini
const sliderMini = [...document.querySelectorAll('.j-slider-mini-wrapper')] as HTMLElement[];

sliderMini.forEach((wrapper: HTMLElement) => {
    initSliderMini(wrapper);
});

// Finishing slider mobile points
const sliderFinishingLists = [...document.querySelectorAll<HTMLElement>('.j-slider-finishing__list-mobile')];

sliderFinishingLists.forEach((list: HTMLElement) => {
    initSliderFinishing(list);
});

// Слайдер вертикальный
const sliderVertical = [...document.querySelectorAll('.j-slider-vertical-wrapper')] as HTMLElement[];

sliderVertical.forEach((wrapper: HTMLElement) => {
    initSliderVertical(wrapper);
});

// Sticky slider
const stickySliders = [...document.querySelectorAll('.j-slider-vertical-sticky')] as HTMLElement[];

stickySliders.forEach((wrapper: HTMLElement) => {
    initStickySlider(wrapper);
});

const sliderVerticalContent = [...document.querySelectorAll('.j-slider-vertical-content-wrapper')] as HTMLElement[];

sliderVerticalContent.forEach((wrapper: HTMLElement) => {
    initSliderVertical(wrapper);
});

// Табы
const tabs = [...document.querySelectorAll('.j-tabs-wrap')];

tabs.forEach((elem: Element) => {
    initTabs(elem as HTMLElement);
});

// Dropdown табов галереи
const galleryDropdowns = [...document.querySelectorAll('.j-gallery-dropdown')] as HTMLElement[];

galleryDropdowns.forEach((elem: HTMLElement) => {
    initGalleryDropdown(elem);
});

// Попапы
const popupSelectors = [...document.querySelectorAll('.j-popup')] as HTMLElement[];

if (popupSelectors.length) {
    initPopup(popupSelectors, {removeDelay: 300});
}

// Отдельный селектор для попапов обратных звонков
const popupFacadeLinks = [...document.querySelectorAll('.j-popup-callback:not([data-init])')] as HTMLElement[];

if (popupFacadeLinks.length) {
    initCallbackPopup(popupFacadeLinks);

    popupFacadeLinks.forEach((button: HTMLElement) => {
        button.dataset['init'] = 'true';
    });
}

// loader
const loader = document.querySelector('.j-loader') as HTMLElement;

if (loader) {
    initLoader(loader);
}

// карта в секции контактов
const contactsMap = document.querySelector('.j-contacts-map') as HTMLElement;

if (contactsMap) {
    import(/* webpackChunkName: "contacts-section" */ '@/sections/contacts')
        .then(({default: initContactsMap}: IDynamicImport) => {
            initContactsMap(contactsMap);
        });
}

// карта в секции location
const locationMap = document.querySelector('.j-location-map') as HTMLElement;

if (locationMap) {
    import(/* webpackChunkName: "location-map" */ '@/sections/location-map')
        .then(({default: initLocationMap}: IDynamicImport) => {
            initLocationMap(locationMap);
        });
}

const infoPoints = [...document.querySelectorAll<HTMLElement>('.j-info-point')];

infoPoints.forEach((point: HTMLElement) => {
    initInfoPoint(point);
});

// Ипотечный калькулятор
const mortgageCalculator = document.querySelector('.j-mortgage-calculator') as HTMLElement;

if (mortgageCalculator) {
    import(/* webpackChunkName: "mortgage-calculator" */ '@/components/mortgage-calculator')
        .then(({default: MortgageCalculator}: IDynamicImport) => {
            const calculator: ClassTypes.IMortgageCalculator = new MortgageCalculator(mortgageCalculator);

            calculator.init();

            // Отображения блока и подстановка месячного платежа ипотеки в карточке квартиры
            const priceMortgageWrappers = [...document.querySelectorAll('.j-price__mortgage')];
            const monthlyPayment = mortgageCalculator.dataset['minMonthly'];

            if (monthlyPayment?.length) {
                priceMortgageWrappers.forEach((wrapper: Element) => {
                    import(/* webpackChunkName: "price-monthly-payment" */ '@/components/price')
                        .then(({default: Price}: IDynamicImport) => {
                            const priceMonthlyPayment: ClassTypes.IPrice = new Price({
                                element: wrapper,
                                payment: monthlyPayment,
                                href   : mortgageCalculator.closest('section')?.id || null
                            });

                            priceMonthlyPayment.init();
                        });
                });
            }
        });
}

// Новости
const newsList = document.querySelector('.j-news') as HTMLElement;

if (newsList) {
    import(/* webpackChunkName: "news-list" */ '@/sections/news')
        .then(({default: NewsList}: IDynamicImport) => {
            new NewsList().init(newsList);
        });
}

// Уведомление об использовании cookies
const cookiesWrapper = document.querySelector('.j-cookies-notice') as HTMLElement;

if (cookiesWrapper) {
    import(/* webpackChunkName: "cookies-notice" */ '@/components/cookies-notice')
        .then(({default: CookiesNotice}: IDynamicImport) => {
            const cookiesNotice: ClassTypes.ICookiesNotice = new CookiesNotice();

            cookiesNotice.init(cookiesWrapper);
        });
}

// Новости
const progressList = document.querySelector('.j-progress') as HTMLElement;

if (progressList) {
    import(/* webpackChunkName: "progress-list" */ '@/sections/progress')
        .then(({default: ProgressList}: IDynamicImport) => {
            new ProgressList().init(progressList);
        });
}

// Простые анимации
const animationsBlocks = [...document.querySelectorAll('.j-animation-block')];

if (animationsBlocks.length) {
    if ('IntersectionObserver' in window) {
        const animationObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry: IntersectionObserverEntry) => {
                if (entry.isIntersecting) {
                    if (!entry.target.classList.contains('is-active')) {
                        entry.target.classList.add('is-active');
                    }
                } else if (entry.target.classList.contains('is-active')) {
                    setTimeout(() => {
                        entry.target.classList.remove('is-active');
                    }, 500);
                }
            });
        });

        animationsBlocks.forEach((block: Element) => {
            animationObserver.observe(block);
        });
    } else {
        animationsBlocks.forEach((block: Element) => {
            block.classList.add('is-active');
        });
    }
}

// Cкролл по якорям
const headerWrapper = headerWrap || document.querySelector('header') as HTMLElement;
const anchors = [...document.querySelectorAll('.j-anchor')] as HTMLElement[];

if (anchors.length) {
    import(/* webpackChunkName: "scrollBy" */ '@/components/scrollBy')
        .then(({default: ScrollBy}: IDynamicImport) => {
            const scrollBy: ClassTypes.IScrollBy = new ScrollBy();
            const CLASS_ACTIVE = 'is-active';

            scrollBy.init({
                takeElements: [headerWrapper],
                anchors
            });

            const setActiveAnchor = (id: string): void => {
                anchors.forEach((item: HTMLElement) => {
                    const href = item.getAttribute('href');

                    item.classList.remove(CLASS_ACTIVE);

                    if (href === id || href === `/${id}`) {
                        item.classList.add(CLASS_ACTIVE);
                    }
                });
            };

            const hash = window.location.hash;

            if (hash.length) {
                setActiveAnchor(hash);
            }

            observer.subscribe('anchorChangeSection', (id: string) => {
                setActiveAnchor(id);
            });
        });
}

const infoSliders = [...document.querySelectorAll('.j-info-section')] as HTMLElement[];

if (infoSliders.length) {
    infoSliders.forEach((wrapper: HTMLElement) => {
        initInfoSlider(wrapper);
    });
}

const flatCards = document.querySelector('.j-flats-card') as HTMLElement;

if (flatCards) {
    initFlatCard(flatCards);
}

const scrollToGalleryTabs = (): void => {
    const galleryTabs = document.getElementById('gallery-tabs');

    if (!galleryTabs) {
        return;
    }

    const headerHeight = headerWrap?.offsetHeight || 0;
    const top = galleryTabs.getBoundingClientRect().top + window.scrollY - headerHeight;

    window.scrollTo({
        top,
        behavior: 'smooth'
    });
};

// async-скрипт может подключиться после DOMContentLoaded / load —
// поэтому ориентируемся на readyState, а не только на событие
const scheduleGalleryTabsScroll = (): void => {
    // Ждём кадр + небольшой таймаут, чтобы успели примениться высота шапки и layout
    requestAnimationFrame(() => {
        setTimeout(scrollToGalleryTabs, 100);
    });
};

if (document.readyState === 'complete') {
    scheduleGalleryTabsScroll();
} else {
    window.addEventListener('load', scheduleGalleryTabsScroll);
}
